import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { routeForMember } from '@/features/auth/hooks';
import { bootstrapAuth } from '@/lib/api';
import type { MemberType } from '@/features/auth/types';

interface Props {
  children: React.ReactNode;
  allowedMemberTypes?: MemberType[];
  /**
   * Where to send an unauthenticated user. Defaults to the chambers-staff login.
   * Solicitor-area routes pass `/solicitor/login` so strangers land at the right door.
   */
  loginRoute?: string;
}

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-600" />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, allowedMemberTypes, loginRoute = '/login' }: Props) {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const location = useLocation();

  // When the store has hydrated but holds NO user, we may still have a valid
  // httpOnly refresh cookie (deep link from an email, localStorage evicted by
  // Safari ITP / privacy settings, etc.). Attempt ONE silent session bootstrap
  // via the cookie before redirecting — otherwise a barrister with a live
  // session gets bounced to /login. A `useRef` guarantees exactly one attempt
  // (a state flag in the deps would re-fire the effect and race the reset);
  // `bootDone` flips only when that single attempt settles.
  const bootStartedRef = useRef(false);
  const [bootDone, setBootDone] = useState(false);

  useEffect(() => {
    if (isHydrated && !user && !bootStartedRef.current) {
      bootStartedRef.current = true;
      bootstrapAuth().finally(() => setBootDone(true));
    }
  }, [isHydrated, user]);

  // Full-screen loader while: (a) the store rehydrates from localStorage, or
  // (b) we have no user yet and the one-shot cookie bootstrap hasn't settled.
  // (If bootstrap succeeds it sets `user`, so we fall straight through to render.)
  if (!isHydrated || (!user && !bootDone)) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to={loginRoute} state={{ from: location }} replace />;
  }

  if (allowedMemberTypes && !allowedMemberTypes.includes(user.memberType)) {
    return <Navigate to={routeForMember(user.memberType)} replace />;
  }

  return <>{children}</>;
}
